import {
  useGetJobProcessingResults,
  useGetJobProcessingResultsAsUser,
  type ErrorResponse,
  type ProcessingResultsResponse,
} from "@taxassist/api-client";

interface UseJobProcessingResultsProps {
  jobId: string;
  context: "client" | "ops";
  firmId?: string;
  enabled?: boolean;
}

interface UseJobProcessingResultsReturn {
  data: ProcessingResultsResponse | undefined;
  isLoading: boolean;
  error: ErrorResponse | null;
  refetch: () => Promise<any>;
}

/**
 * Unified hook for fetching job processing results
 *
 * Automatically selects the correct API endpoint based on context:
 * - 'ops': Uses admin endpoint (useGetJobProcessingResults)
 * - 'client': Uses user endpoint (useGetJobProcessingResultsAsUser)
 *
 * @param params - Configuration for the processing results query
 * @returns Unified processing results data and query state
 */
export function useJobProcessingResults({
  jobId,
  context,
  firmId,
  enabled = true,
}: UseJobProcessingResultsProps): UseJobProcessingResultsReturn {
  // Ops context - use admin endpoint
  const opsQuery = useGetJobProcessingResults(jobId, {
    query: {
      enabled: enabled && context === "ops" && !!jobId,
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
      queryKey: ["jobProcessingResults", "ops", jobId],
    },
  });

  // Client context - use user endpoint
  const clientQuery = useGetJobProcessingResultsAsUser(firmId!, jobId, {
    query: {
      enabled: enabled && context === "client" && !!jobId && !!firmId,
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
      queryKey: ["jobProcessingResults", "client", firmId, jobId],
    },
  });

  // Return the appropriate query result based on context
  // Both API endpoints should return the same data structure
  if (context === "ops") {
    return {
      data: opsQuery.data,
      isLoading: opsQuery.isLoading,
      error: opsQuery.error,
      refetch: opsQuery.refetch,
    };
  } else {
    return {
      data: clientQuery.data,
      isLoading: clientQuery.isLoading,
      error: clientQuery.error,
      refetch: clientQuery.refetch,
    };
  }
}
